import type { CompanyFootprint } from '@/api/(admin)/companies/types';

/** One line of the Tech Insights table: a table and its rows for the company. */
export interface FootprintRow {
  id: string;
  /** The module it belongs to, already translated. */
  module: string;
  table: string;
  /** `null` = too large to count quickly. */
  rows: number | null;
}

export interface TechInsightCategory {
  label: string;
  /** Summed row count across the module's tables. */
  total: number;
  /** The tables behind that total, for the legend's tooltip. */
  tables: string[];
}

/**
 * Every counted table, module by module, then the two activity counts. The
 * endpoint already leaves out tables with nothing in them; an activity count of
 * zero is left out the same way.
 */
export function footprintRows(
  footprint: CompanyFootprint,
  moduleLabel: (groupKey: string) => string,
  activityLabel: string,
): FootprintRow[] {
  const rows: FootprintRow[] = footprint.groups.flatMap(group =>
    group.tables.map(table => ({
      id: `${group.key}:${table.table}`,
      module: moduleLabel(group.key),
      table: table.table,
      rows: table.rows,
    })),
  );
  const activity: [string, number | null][] = [
    ['user_tokens', footprint.activity.userTokens],
    ['app_notifications', footprint.activity.appNotifications],
  ];
  for (const [table, count] of activity) {
    if (count === 0) continue;
    rows.push({
      id: `activity:${table}`,
      module: activityLabel,
      table,
      rows: count,
    });
  }
  return rows;
}

/**
 * One chart spoke per module that has rows. Modules with nothing would draw
 * empty spokes, and most companies use only a few of the sixteen.
 */
export function footprintCategories(
  footprint: CompanyFootprint,
  moduleLabel: (groupKey: string) => string,
): TechInsightCategory[] {
  return footprint.groups
    .filter(group => (group.rows ?? 0) > 0)
    .map(group => ({
      label: moduleLabel(group.key),
      total: group.rows ?? 0,
      tables: group.tables.map(table => table.table),
    }));
}
